import { PositionKind } from "@/services/backend/omni/position/types/position-kind";
import { PositionUnit } from "@/services/backend/omni/position/types/position-unit";
import {
  newPriceModifier,
  PriceModifier,
  PriceModifierKind,
  PriceModifierSchema,
} from "@/services/backend/omni/pricemodifier/types/price-modifier";

/** Mirrors `pricing.Pricing`, backend/core/omni/domain/pricing/pricing.go. */
export type Pricing = {
  active: boolean;
  usePositions: boolean;
  items: PricingItem[];
  dynamicItems: DynamicItem[];
};

/** Mirrors `pricing.Item`, backend/core/omni/domain/pricing/item.go. */
export type PricingItem = {
  order: number;
  parentIdx: number;
  kind: PricingItemKind;
  text: string;
  priceModifier: PriceModifier;
  price: number;
};

/** Mirrors `pricing.ItemKind`, backend/core/omni/domain/pricing/item_kind.go. */
export enum PricingItemKind {
  Default = "",
  Modifier = "modifier",
  SubTotal = "subtotal",
  Total = "total",
  Text = "text",
}

/** Mirrors `pricing.DynamicItem`, backend/core/omni/domain/pricing/item.go. */
export type DynamicItem = {
  order: number;
  parentIdx: number;
  kind: PositionKind;
  text: string;
  unit: PositionUnit | "";
  unitPrice: number;
};

/** Mirrors `pricing.PricingPatch`, backend/core/omni/domain/pricing/pricing.go. */
export type PricingPatch = {
  active?: boolean;
  usePositions?: boolean;
  items?: PricingItem[];
  dynamicItems?: DynamicItem[];
};

/** Mirrors `pricing.By`, backend/core/omni/domain/pricing/by.go. */
export type PricingBy = {
  dimension: PricingByDimension;
  active: boolean;
  usePositions: boolean;
  items: ByItem[];
};

/** Mirrors `pricing.Dimension`, backend/core/omni/domain/pricing/by.go. */
export enum PricingByDimension {
  Area = "area",
  Group = "group",
}

/** Mirrors `pricing.ByItem`, backend/core/omni/domain/pricing/by_item.go. */
export type ByItem = {
  order: number;
  kind: ByItemKind;
  text: string;
  columns?: Column[];
  total: number;
};

/** Mirrors `pricing.ByItemKind`, backend/core/omni/domain/pricing/by_item.go. */
export enum ByItemKind {
  Default = "",
  Total = "total",
}

/** Mirrors `pricing.Column`, backend/core/omni/domain/pricing/by_item.go. */
export type Column = {
  title: string;
  kind: ColumnKind;
  value: number;
};

/** Mirrors `pricing.ColumnKind`, backend/core/omni/domain/pricing/by_item.go. */
export enum ColumnKind {
  Default = "",
  Modifier = "modifier",
  SubTotal = "subtotal",
}

/** Mirrors `pricing.ByPatch`, backend/core/omni/domain/pricing/by.go. */
export type PricingByPatch = {
  active?: boolean;
  usePositions?: boolean;
  dimension?: PricingByDimension;
  items?: ByItem[];
  item?: ByItem;
  removeItem?: number;
  addItem?: ByItem;
};

/**
 * calculatePricingItems mirrors the calculation of `pricing.Pricing`
 * for an optimistic update: it derives the base of relative modifiers,
 * the prices of modifiers, sub totals and the total from the running sum
 * of the top-level items above them.
 */
export function calculatePricingItems(items: PricingItem[]): PricingItem[] {
  let sum = 0;
  return items.map((item) => {
    let calculated = item;
    switch (item.kind) {
      case PricingItemKind.Modifier: {
        const m = item.priceModifier;
        const relativeBase =
          m.schema === PriceModifierSchema.Relative ? sum : m.relativeBase;
        const value =
          m.schema === PriceModifierSchema.Relative
            ? (relativeBase * m.relativeRate) / 100
            : m.value;
        calculated = {
          ...item,
          priceModifier: { ...m, relativeBase, value },
          price: m.active ? value : 0,
        };
        break;
      }
      case PricingItemKind.Text:
        calculated = { ...item, price: 0 };
        break;
      case PricingItemKind.SubTotal:
      case PricingItemKind.Total:
        if (item.parentIdx === 0) {
          calculated = { ...item, price: sum };
        }
        break;
    }
    if (
      calculated.parentIdx === 0 &&
      (calculated.kind === PricingItemKind.Default ||
        calculated.kind === PricingItemKind.Modifier)
    ) {
      sum += calculated.price;
    }
    return calculated;
  });
}

/** newPricingItem returns an item of kind without text and price. */
export function newPricingItem(
  kind: PricingItemKind,
  modifier: Pick<PriceModifier, "kind" | "schema"> = {
    kind: PriceModifierKind.Discount,
    schema: PriceModifierSchema.Relative,
  },
): PricingItem {
  return {
    order: 0,
    parentIdx: 0,
    kind,
    text: "",
    priceModifier:
      kind === PricingItemKind.Modifier
        ? { ...newPriceModifier(), ...modifier }
        : zeroPriceModifier(),
    price: 0,
  };
}

function zeroPriceModifier(): PriceModifier {
  return {
    active: false,
    title: "",
    kind: "" as PriceModifierKind,
    schema: "" as PriceModifierSchema,
    relativeBase: 0,
    relativeRate: 0,
    value: 0,
  };
}

/** updatePricingItem replaces the item of order and recalculates. */
export function updatePricingItem(
  items: PricingItem[],
  order: number,
  item: PricingItem,
): PricingItem[] {
  return calculatePricingItems(
    items.map((i) => (i.order === order ? { ...item, order } : i)),
  );
}

/**
 * removePricingItem removes the item of order with its children,
 * renumbers the items and recalculates.
 */
export function removePricingItem(
  items: PricingItem[],
  order: number,
): PricingItem[] {
  const entries = toEntries(items).filter(
    (e) => e.item.order !== order && e.parent?.order !== order,
  );
  return calculatePricingItems(fromEntries(entries));
}

/**
 * addPricingItemBelow inserts item below the item of order: after its
 * children for a top-level item, as a sibling child below a child, at
 * the end for an unknown order. The total is always kept last; a second
 * total is not added.
 */
export function addPricingItemBelow(
  items: PricingItem[],
  order: number,
  item: PricingItem,
): PricingItem[] {
  if (item.kind === PricingItemKind.Total) {
    if (items.some(isTotal)) {
      return items;
    }
    return calculatePricingItems(
      fromEntries([...toEntries(items), { item, parent: null }]),
    );
  }

  return calculatePricingItems(
    addOrderedItemBelow(items, order, item, isTotal),
  );
}

/**
 * movePricingItem moves the item activeOrder to the place of the item
 * overOrder. A top-level item moves with its children, onto the place of
 * the top-level item over belongs to. A child is re-parented to the
 * nearest top-level item above its new place, or becomes top-level
 * without one. The total cannot be moved and stays last.
 */
export function movePricingItem(
  items: PricingItem[],
  activeOrder: number,
  overOrder: number,
): PricingItem[] {
  const moved = moveOrderedItem(items, activeOrder, overOrder, isTotal);
  if (moved === items) {
    return items;
  }

  return calculatePricingItems(moved);
}

function isTotal(item: PricingItem): boolean {
  return item.kind === PricingItemKind.Total;
}

/**
 * newDynamicItem returns a dynamic item of kind without text, unit and
 * price. kind is a dynamic position, priced per unit, or a text.
 */
export function newDynamicItem(
  kind: typeof PositionKind.Dynamic | typeof PositionKind.Text,
): DynamicItem {
  return { order: 0, parentIdx: 0, kind, text: "", unit: "", unitPrice: 0 };
}

/** updateDynamicItem replaces the dynamic item of order. */
export function updateDynamicItem(
  items: DynamicItem[],
  order: number,
  item: DynamicItem,
): DynamicItem[] {
  return items.map((i) => (i.order === order ? { ...item, order } : i));
}

/**
 * removeDynamicItem removes the dynamic item of order with its children
 * and renumbers the items.
 */
export function removeDynamicItem(
  items: DynamicItem[],
  order: number,
): DynamicItem[] {
  const entries = toEntries(items).filter(
    (e) => e.item.order !== order && e.parent?.order !== order,
  );
  return fromEntries(entries);
}

/**
 * addDynamicItemBelow inserts item below the dynamic item of order, see
 * {@link addPricingItemBelow}; at the end for an unknown order.
 */
export function addDynamicItemBelow(
  items: DynamicItem[],
  order: number,
  item: DynamicItem,
): DynamicItem[] {
  return addOrderedItemBelow(items, order, item, () => false);
}

/**
 * moveDynamicItem moves the dynamic item activeOrder to the place of the
 * item overOrder, see {@link movePricingItem}.
 */
export function moveDynamicItem(
  items: DynamicItem[],
  activeOrder: number,
  overOrder: number,
): DynamicItem[] {
  return moveOrderedItem(items, activeOrder, overOrder, () => false);
}

/**
 * calculatePricingByItems mirrors the calculation of `pricing.By` for an
 * optimistic update: a subtotal column sums the columns before it, an
 * item's total sums its default and modifier columns, and the total item
 * sums the totals of all other items.
 */
export function calculatePricingByItems(items: ByItem[]): ByItem[] {
  const calculated = items.map((item) => {
    let sum = 0;
    const columns = item.columns?.map((column) => {
      if (column.kind === ColumnKind.SubTotal) {
        return { ...column, value: sum };
      }
      sum += column.value;
      return column;
    });
    return { ...item, columns, total: sum };
  });

  const total = calculated
    .filter((i) => i.kind !== ByItemKind.Total)
    .reduce((sum, i) => sum + i.total, 0);
  return calculated.map((i) =>
    i.kind === ByItemKind.Total ? { ...i, total } : i,
  );
}

/**
 * pricingByColumns returns the column set of the items: the columns of
 * the first item which is not the total, with values 0, or a single
 * price column if no item has columns yet.
 */
export function pricingByColumns(items: ByItem[]): Column[] {
  const first = items.find(
    (i) => i.kind !== ByItemKind.Total && (i.columns?.length ?? 0) > 0,
  );
  if (!first?.columns) {
    return [{ title: "Preis", kind: ColumnKind.Default, value: 0 }];
  }

  return first.columns.map((c) => ({ ...c, value: 0 }));
}

/** newByItem returns an item of kind; the total has no columns. */
export function newByItem(kind: ByItemKind, columns: Column[]): ByItem {
  return {
    order: 0,
    kind,
    text: "",
    columns: kind === ByItemKind.Total ? [] : columns,
    total: 0,
  };
}

/** updateByItem replaces the item of order and recalculates. */
export function updateByItem(
  items: ByItem[],
  order: number,
  item: ByItem,
): ByItem[] {
  return calculatePricingByItems(
    items.map((i) => (i.order === order ? { ...item, order } : i)),
  );
}

/** removeByItem removes the item of order, renumbers and recalculates. */
export function removeByItem(items: ByItem[], order: number): ByItem[] {
  return calculatePricingByItems(
    renumberByItems(items.filter((i) => i.order !== order)),
  );
}

/**
 * addByItemBelow inserts item below the item of order, at the end for an
 * unknown order. The total is always kept last; a second total is not
 * added.
 */
export function addByItemBelow(
  items: ByItem[],
  order: number,
  item: ByItem,
): ByItem[] {
  const sorted = [...items].sort((a, b) => a.order - b.order);
  const totalIdx = sorted.findIndex((i) => i.kind === ByItemKind.Total);
  if (item.kind === ByItemKind.Total) {
    if (totalIdx >= 0) {
      return items;
    }
    return calculatePricingByItems(renumberByItems([...sorted, item]));
  }

  const idx = sorted.findIndex((i) => i.order === order);
  const at = idx < 0 ? sorted.length : idx + 1;
  const lastAllowed = totalIdx >= 0 ? totalIdx : sorted.length;
  sorted.splice(Math.min(at, lastAllowed), 0, item);
  return calculatePricingByItems(renumberByItems(sorted));
}

/**
 * moveByItem moves the item activeOrder to the place of the item
 * overOrder. The total can be neither moved nor dropped onto. It returns
 * items unchanged if nothing moves.
 */
export function moveByItem(
  items: ByItem[],
  activeOrder: number,
  overOrder: number,
): ByItem[] {
  const sorted = [...items].sort((a, b) => a.order - b.order);
  const from = sorted.findIndex((i) => i.order === activeOrder);
  const to = sorted.findIndex((i) => i.order === overOrder);
  if (
    from < 0 ||
    to < 0 ||
    from === to ||
    sorted[from].kind === ByItemKind.Total ||
    sorted[to].kind === ByItemKind.Total
  ) {
    return items;
  }

  sorted.splice(to, 0, ...sorted.splice(from, 1));
  return calculatePricingByItems(renumberByItems(sorted));
}

function renumberByItems(items: ByItem[]): ByItem[] {
  return items.map((i, idx) => ({ ...i, order: idx + 1 }));
}

type Ordered = { order: number; parentIdx: number };

type Entry<T extends Ordered> = { item: T; parent: T | null };

// addOrderedItemBelow inserts item after the block of the item of order,
// as a sibling child below a child, at the end for an unknown order. An
// item for which isFixedLast holds stays last.
function addOrderedItemBelow<T extends Ordered>(
  items: T[],
  order: number,
  item: T,
  isFixedLast: (item: T) => boolean,
): T[] {
  const entries = toEntries(items);

  // find the place below the item of order and its children
  const idx = entries.findIndex((e) => e.item.order === order);
  const below = idx < 0 ? null : entries[idx];
  let at = entries.length;
  let parent: T | null = null;
  if (below) {
    parent = below.parent;
    at = idx + 1;
    if (!below.parent) {
      while (at < entries.length && entries[at].parent === below.item) {
        at++;
      }
    }
  }

  // never insert after a fixed last item
  const fixedIdx = entries.findIndex((e) => isFixedLast(e.item));
  const lastAllowed = fixedIdx >= 0 ? fixedIdx : entries.length;
  entries.splice(Math.min(at, lastAllowed), 0, { item, parent });

  return fromEntries(entries);
}

// moveOrderedItem moves the item activeOrder to the place of overOrder,
// see movePricingItem. It returns items unchanged if nothing moves. An
// item for which isFixedLast holds can't be moved and stays last.
function moveOrderedItem<T extends Ordered>(
  items: T[],
  activeOrder: number,
  overOrder: number,
  isFixedLast: (item: T) => boolean,
): T[] {
  const entries = toEntries(items);
  const active = entries.find((e) => e.item.order === activeOrder);
  const over = entries.find((e) => e.item.order === overOrder);
  if (!active || !over || active === over || isFixedLast(active.item)) {
    return items;
  }
  const fixed = entries.find((e) => isFixedLast(e.item));

  let moved: Entry<T>[];
  if (!active.parent) {
    // a top-level item moves with its children, block by block
    const blocks = toBlocks(entries);
    const from = blocks.findIndex((b) => b[0] === active);
    let to = blocks.findIndex((b) => b.includes(over));
    if (fixed && blocks[to][0] === fixed) {
      to -= 1;
    }
    if (from === to) {
      return items;
    }
    blocks.splice(to, 0, ...blocks.splice(from, 1));
    moved = blocks.flat();
  } else {
    // a child moves alone and joins the nearest top-level item above
    const from = entries.indexOf(active);
    let to = entries.indexOf(over);
    if (over === fixed) {
      to -= 1;
    }
    moved = [...entries];
    moved.splice(to, 0, ...moved.splice(from, 1));
    let parent: T | null = null;
    moved = moved.map((e) => {
      if (!e.parent && e !== active) {
        parent = e.item;
        return e;
      }
      return e === active ? { ...e, parent } : e;
    });
  }

  return fromEntries(moved);
}

function toEntries<T extends Ordered>(items: T[]): Entry<T>[] {
  const byOrder = new Map(items.map((i) => [i.order, i]));
  return [...items]
    .sort((a, b) => a.order - b.order)
    .map((item) => ({
      item,
      parent: item.parentIdx ? (byOrder.get(item.parentIdx) ?? null) : null,
    }));
}

function toBlocks<T extends Ordered>(entries: Entry<T>[]): Entry<T>[][] {
  const blocks: Entry<T>[][] = [];
  for (const e of entries) {
    if (!e.parent || blocks.length === 0) {
      blocks.push([e]);
    } else {
      blocks[blocks.length - 1].push(e);
    }
  }
  return blocks;
}

function fromEntries<T extends Ordered>(entries: Entry<T>[]): T[] {
  const orderOf = new Map(entries.map((e, i) => [e.item, i + 1]));
  return entries.map((e, i) => ({
    ...e.item,
    order: i + 1,
    parentIdx: e.parent ? (orderOf.get(e.parent) ?? 0) : 0,
  }));
}

/**
 * patchPricing returns pricing with the fields set in patch applied, e.g.
 * for an optimistic cache update.
 */
export function patchPricing(pricing: Pricing, patch?: PricingPatch): Pricing {
  if (!patch) {
    return pricing;
  }

  return {
    ...pricing,
    active: patch.active ?? pricing.active,
    usePositions: patch.usePositions ?? pricing.usePositions,
    items: patch.items ?? pricing.items,
    dynamicItems: patch.dynamicItems ?? pricing.dynamicItems,
  };
}

/**
 * patchPricingBy returns by with patch applied, e.g. for an optimistic
 * cache update: replaced, updated, removed (renumbering the rest) and
 * added items.
 */
export function patchPricingBy(
  by: PricingBy,
  patch?: PricingByPatch,
): PricingBy {
  if (!patch) {
    return by;
  }

  let items = patch.items ?? by.items;
  const item = patch.item;
  if (item) {
    items = items.map((i) => (i.order === item.order ? item : i));
  }
  const removeItem = patch.removeItem;
  if (removeItem !== undefined) {
    items = items
      .filter((i) => i.order !== removeItem)
      .map((i, idx) => ({ ...i, order: idx + 1 }));
  }
  if (patch.addItem) {
    items = [...items, { ...patch.addItem, order: items.length + 1 }];
  }

  return {
    ...by,
    active: patch.active ?? by.active,
    usePositions: patch.usePositions ?? by.usePositions,
    dimension: patch.dimension ?? by.dimension,
    items,
  };
}
